/** Shared plumbing for use cases. Not part of the UI contract. */
import { TEMPLATE_META, type TemplateCall } from '../../adapters/messaging/templates';
import type { AuthAdapter, Clock, MessagingAdapter, PaymentsAdapter, SendRequest, StorageAdapter } from '../../adapters/types';
import type { Links } from '../../config/env';
import { formatNis } from '../money';
import { localeTag } from '../../i18n';
import { formatDate, formatTime, toMs } from '../time';
import { resolvePlan, type ResolvedPlan } from '../trial';
import {
  decideDelivery, planAppointmentMessages, type AppointmentEvent, type MessagePlan, type PlanText,
} from '../reminders';
import type { AvailabilityData } from '../availability';
import type {
  Appointment, Business, Client, Id, Locale, Message, Service, Settings, Staff, TimeOff, WeeklyHours,
} from '../types';

export interface Deps {
  storage: StorageAdapter;
  messaging: MessagingAdapter;
  payments: PaymentsAdapter;
  auth: AuthAdapter;
  clock: Clock;
  links: Links;
  locale: () => Locale;
}

export interface Bundle {
  business: Business;
  settings: Settings;
  staff: Staff[];
  services: Service[];
  weeklyHours: WeeklyHours[];
  timeOff: TimeOff[];
  plan: ResolvedPlan;
}

export async function loadBundle(d: Deps, businessId: Id): Promise<Bundle | undefined> {
  const business = await d.storage.repo('businesses').get(businessId);
  const settings = await d.storage.repo('settings').get(businessId);
  if (!business || !settings) return undefined;
  const where = { businessId };
  return {
    business, settings,
    staff: await d.storage.repo('staff').list({ where }),
    services: await d.storage.repo('services').list({ where }),
    weeklyHours: await d.storage.repo('weeklyHours').list({ where }),
    timeOff: await d.storage.repo('timeOff').list({ where }),
    plan: resolvePlan(business, d.clock.now()),
  };
}

/** Finds a business by its current slug, or by a retired slug that is still inside its alias window. */
export async function findBusinessBySlug(d: Deps, slug: string): Promise<Business | undefined> {
  const [current] = await d.storage.repo('businesses').list({ where: { slug } });
  if (current) return current;
  const nowMs = d.clock.now();
  return (await d.storage.repo('businesses').list({ filter: (b) => (b.slugAliases ?? []).some((a) => a.slug === slug && Date.parse(a.until) > nowMs) }))[0];
}

/** Public (client-facing) lookup: an account scheduled for deletion is closed to clients, so it behaves as if it did not exist. */
export async function loadBundleBySlug(d: Deps, slug: string): Promise<Bundle | undefined> {
  const business = await findBusinessBySlug(d, slug);
  return business && !business.deletion ? loadBundle(d, business.id) : undefined;
}

/** loadBundle for client-facing flows (booking, slots, waitlist): undefined while the account is scheduled for deletion. */
export async function loadPublicBundle(d: Deps, businessId: Id): Promise<Bundle | undefined> {
  const b = await loadBundle(d, businessId);
  return b && !b.business.deletion ? b : undefined;
}

export const availabilityData = (b: Bundle, appointments: Appointment[]): AvailabilityData => ({
  tz: b.business.tz, settings: b.settings, staff: b.staff, weeklyHours: b.weeklyHours, timeOff: b.timeOff, appointments,
});

export const listAppointments = (d: Deps, businessId: Id): Promise<Appointment[]> =>
  d.storage.repo('appointments').list({ where: { businessId } });

const TOKEN_ALPHABET = 'abcdefghijkmnpqrstuvwxyz23456789';
/** Unguessable 22-character link token (~110 bits). */
export function makeToken(): string {
  const bytes = new Uint8Array(22);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => TOKEN_ALPHABET.charAt(b % TOKEN_ALPHABET.length)).join('');
}

export function planText(d: Deps, b: Bundle, payUrl?: string): PlanText {
  const tag = localeTag(b.business.locale);
  const tz = b.business.tz;
  return {
    businessName: b.business.name,
    date: (ms) => formatDate(ms, 'long', tag, tz),
    time: (ms) => formatTime(ms, tag, tz),
    relativeDay: (n) => new Intl.RelativeTimeFormat(tag, { numeric: 'auto' }).format(n, 'day'),
    money: (a) => formatNis(a, tag),
    manageUrl: (token) => d.links.manage(token),
    bookUrl: d.links.booking(b.business.slug),
    payUrl: payUrl ?? d.links.booking(b.business.slug),
  };
}

/** Client-triggered templates: sent at once. Owner-initiated ones (rescheduled, cancelled_by_owner) and reminders stay queued for the undo window / scheduler. */
const IMMEDIATE: ReadonlySet<string> = new Set(['booking_confirmed', 'deposit_request', 'deposit_received', 'deposit_expired', 'cancelled_by_client_ack', 'deposit_claimed_owner']);

/** Persists a message plan: cancels stale messages, then sends or suppresses each new one. */
export async function dispatchPlan(d: Deps, businessId: Id, plan: MessagePlan): Promise<Message[]> {
  for (const id of plan.cancelIds) await d.messaging.cancel(id);
  const out: Message[] = [];
  for (const p of plan.create) {
    const req: SendRequest = {
      businessId, to: p.to, call: p.call, clientId: p.clientId, appointmentId: p.appointmentId, purpose: p.purpose,
      dedupeKey: p.dedupeKey, scheduledFor: p.delivery.scheduledFor, deferred: p.delivery.status === 'queued' && p.delivery.deferredFrom !== undefined,
    };
    if (p.delivery.status !== 'queued') { out.push(await d.messaging.suppress(req, p.delivery.reason)); continue; }
    let sent = await d.messaging.send(req);
    // Due now (not deferred by quiet hours): dispatch at once so the outbox shows it sent without waiting for a scheduler tick.
    if (sent.status === 'queued' && IMMEDIATE.has(req.call.template) && Date.parse(sent.scheduledFor) <= d.clock.now() && d.messaging.dispatchNow) sent = (await d.messaging.dispatchNow(sent.id)) ?? sent;
    out.push(sent);
  }
  return out;
}

/** Plans and dispatches the messages for an appointment event (consent, plan, quiet hours applied). */
export async function notifyAppointment(
  d: Deps, event: AppointmentEvent, appointment: Appointment, extra: { previousStart?: string; payUrl?: string } = {},
): Promise<Message[]> {
  const b = await loadBundle(d, appointment.businessId);
  const client = await d.storage.repo('clients').get(appointment.clientId);
  const service = b?.services.find((s) => s.id === appointment.serviceId);
  if (!b || !client || !service || client.deletedAt) return [];
  const where = { businessId: b.business.id };
  const plan = planAppointmentMessages({
    event, appointment, client, service, settings: b.settings, plan: b.plan, tz: b.business.tz,
    consents: await d.storage.repo('consents').list({ where }),
    messages: await d.storage.repo('messages').list({ where }),
    nowMs: d.clock.now(), text: planText(d, b, extra.payUrl), ...(extra.previousStart ? { previousStart: extra.previousStart } : {}),
  });
  return dispatchPlan(d, b.business.id, plan);
}

/** Sends one non-appointment message (waitlist offers) through the same consent/plan/quiet-hours gate. */
export async function sendDirect(
  d: Deps, b: Bundle, client: Client, call: TemplateCall, dedupeKey: string,
  opts: { appointmentId?: Id; appointmentStart?: string } = {},
): Promise<Message | undefined> {
  const where = { businessId: b.business.id };
  const delivery = decideDelivery({
    template: call.template, clientId: client.id, consents: await d.storage.repo('consents').list({ where }),
    plan: b.plan, settings: b.settings, tz: b.business.tz, messages: await d.storage.repo('messages').list({ where }),
    desiredAtMs: d.clock.now(), nowMs: d.clock.now(), ...(opts.appointmentStart ? { appointmentStartMs: toMs(opts.appointmentStart) } : {}),
  });
  if (delivery.status === 'skip') return undefined;
  const req: SendRequest = {
    businessId: b.business.id, to: client.phone, call, clientId: client.id, purpose: TEMPLATE_META[call.template].purpose, dedupeKey,
    scheduledFor: delivery.scheduledFor, ...(opts.appointmentId ? { appointmentId: opts.appointmentId } : {}),
    deferred: delivery.status === 'queued' && delivery.deferredFrom !== undefined,
  };
  return delivery.status === 'queued' ? d.messaging.send(req) : d.messaging.suppress(req, delivery.reason);
}

export const firstName = (name: string): string => name.trim().split(/\s+/)[0] ?? name;
